// FinanceAlerts — variance & anomaly surface, focused on the two budget lenses
// the board governs by: COMMITTEE budgets (asks vs the proposed FY27 v2 budget,
// 06-JUL-2026 revision — Board adoption pending) and OCA / object budgets
// (projected overruns incl. open encumbrance, and big year-over-year swings),
// plus supporting vendor anomalies from the scheduled finance-analysis engine.
// Board/staff (view_finance).

import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  useObjectWatch,
  useFinanceHistory,
  useVendors,
  useCommitteeBudget,
  useFinanceAnalysis,
} from "../hooks/useFinance";
import type {
  ObjectWatchRow,
  HistoryObjectRow,
  VendorRow,
  FinanceSignal,
  CommitteeBudgetLine,
} from "../hooks/useFinance";
import KPICard from "../components/dashboard/KPICard";
import ChartCard from "../components/dashboard/ChartCard";
import InfoTip from "../components/shared/InfoTip";
import PageIntro, { type VerdictTone } from "../components/shared/PageIntro";
import StatusLegend from "../components/shared/StatusLegend";
import PageShell from "../components/shared/PageShell";
import PageHeader from "../components/shared/PageHeader";
import { asNum, usd0 } from "../components/finance/charts";
import {
  TH,
  THR,
  StatusTitle,
  FetchErrorBanner,
} from "../components/finance/ui";
import {
  currentFiscalYear,
  priorFiscalYear,
  maxFiscalYear,
  fyContext,
} from "../lib/fiscal";
import PriorYearBanner from "../components/finance/PriorYearBanner";

// ── Committee ask variance ────────────────────────────────────────────────────
// C5 (2026-08-16): this card used to compute delta = adjusted + projects −
// proposed off an endpoint that served the SAME v2 sum as both proposed and
// adjusted with projects NULL. delta was (x + 0) − x = 0 for every committee,
// the ≥$15K filter emptied the list by construction, and the card printed a
// GREEN "no committee asks diverge" all-clear that could never fail. The
// endpoint now serves the real ask (dash_finance_committee_budget) against the
// v2 baseline, and marks each committee comparable / stale / missing. Variance
// is computed for COMPARABLE committees only: differencing an ask captured
// before the 24-JUN/06-JUL v2 revisions against the post-revision baseline
// would publish the revision itself as a "committee divergence". When nothing
// is comparable the card says so — it never renders that as green or as zero.
export type CommitteeAskState = "comparable" | "stale" | "missing";

export interface CommitteeVarianceRow {
  committee: string;
  baseline: number;
  ask: number;
  delta: number;
  pct: number;
  status: string | null;
}

export interface CommitteeVarianceView {
  /** Rows over the materiality threshold — comparable committees only. */
  variances: CommitteeVarianceRow[];
  comparable: number;
  stale: number;
  missing: number;
  /** Committees with no comparable ask, named so the board can see the gap. */
  uncomparableCommittees: string[];
  /** True only when at least one committee can actually be checked. */
  computable: boolean;
  /** Latest ask edit on file / latest baseline revision (YYYY-MM-DD). */
  askAsOf: string | null;
  baselineAsOf: string | null;
  committeesTotal: number;
}

const asDate = (v: unknown): string | null =>
  typeof v === "string" && v.length >= 10 ? v.slice(0, 10) : null;

export function committeeVarianceView(
  lines: CommitteeBudgetLine[] | undefined,
): CommitteeVarianceView {
  const rows = lines ?? [];
  const states = rows.map((l) => ({
    committee: String(l.committee),
    baseline: asNum(l.baseline_fy27 ?? l.proposed_fy27),
    ask: l.ask_total == null ? null : asNum(l.ask_total),
    state: (l.ask_state as CommitteeAskState | undefined) ?? "missing",
    askAsOf: asDate(l.ask_updated_at),
    baselineAsOf: asDate(l.baseline_updated_at ?? l.updated_at),
    status: (l.status as string | null) ?? null,
  }));

  const comparable = states.filter(
    (r) => r.state === "comparable" && r.ask !== null,
  );
  const variances = comparable
    .map((r) => {
      const baseline = r.baseline;
      const ask = r.ask ?? 0;
      const delta = ask - baseline;
      const pct =
        baseline > 0 ? Math.round((delta / baseline) * 100) : ask > 0 ? 999 : 0;
      return {
        committee: r.committee,
        baseline,
        ask,
        delta,
        pct,
        status: r.status,
      };
    })
    .filter(
      (r) =>
        Math.abs(r.delta) >= 15_000 && (r.pct === 999 || Math.abs(r.pct) >= 15),
    )
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  const maxOf = (vals: (string | null)[]) =>
    vals
      .filter((v): v is string => v != null)
      .sort()
      .pop() ?? null;

  return {
    variances,
    comparable: comparable.length,
    stale: states.filter((r) => r.state === "stale").length,
    missing: states.filter((r) => r.state === "missing").length,
    uncomparableCommittees: states
      .filter((r) => r.state !== "comparable" || r.ask === null)
      .map((r) => r.committee),
    computable: comparable.length > 0,
    askAsOf: maxOf(states.map((r) => r.askAsOf)),
    baselineAsOf: maxOf(states.map((r) => r.baselineAsOf)),
    committeesTotal: states.length,
  };
}

/**
 * What the card renders when NO committee ask can be compared to the baseline.
 * This is the honest replacement for the old green "no committee asks diverge"
 * line: it says the check did not run, why, and names the committees it cannot
 * speak for. It must never read as an all-clear.
 */
export function CommitteeVarianceUnavailable({
  view,
}: {
  view: CommitteeVarianceView;
}) {
  return (
    <div className="rounded-xl border border-status-amber/40 bg-status-amber/5 px-4 py-3">
      <p className="text-sm font-semibold text-status-amber">
        Not computable — this is NOT an all-clear.
      </p>
      <p className="mt-1 text-sm text-theme-secondary">
        Ember cannot say whether any committee ask diverges from the FY27 v2
        budget, because no committee has an ask on file that post-dates the
        baseline it would be measured against. No variance figure is shown
        rather than a figure that would be an artifact of the budget being
        revised after the asks were captured.
      </p>
      <ul className="mt-2 space-y-1 text-xs text-theme-muted">
        <li>
          Committees on the v2 baseline: {view.committeesTotal} · comparable
          asks: {view.comparable} · asks predating the baseline: {view.stale} ·
          no ask on file: {view.missing}
        </li>
        <li>
          Latest ask on file: {view.askAsOf ?? "none"} · latest v2 baseline
          revision: {view.baselineAsOf ?? "unknown"}
        </li>
        {view.uncomparableCommittees.length > 0 && (
          <li>Not covered: {view.uncomparableCommittees.join(", ")}</li>
        )}
      </ul>
      <p className="mt-2 text-xs text-theme-muted">
        This clears itself the moment a committee ask is recorded against the v2
        budget — it is not a permanent suppression.
      </p>
    </div>
  );
}

// ── OCA projected overruns ────────────────────────────────────────────────────
// C5 (2026-08-16): this list filtered `budget > 0`, which DROPPED every object
// that overran with no appropriation at all. On the live FY27/2119/656500 pull
// that concealed four unbudgeted objects. An object spending against a $0
// budget is the most serious kind of overrun, not one to omit — it is included
// and labelled UNBUDGETED. The API already flags these `over` (ratio Infinity,
// pct_projected 999); nothing here changes any figure.
export function overrunRows(rows: ObjectWatchRow[] | undefined): {
  rows: ObjectWatchRow[];
  unbudgeted: ObjectWatchRow[];
  unbudgetedTotal: number;
} {
  const over = (rows ?? [])
    .filter((r) => r.flag === "over")
    .sort((a, b) => b.pct_projected - a.pct_projected)
    .slice(0, 15);
  const unbudgeted = over.filter((r) => asNum(r.budget) <= 0);
  return {
    rows: over,
    unbudgeted,
    unbudgetedTotal: unbudgeted.reduce((a, r) => a + asNum(r.projected), 0),
  };
}

export default function FinanceAlerts() {
  const { data: watch, isLoading, error } = useObjectWatch();
  const { data: history } = useFinanceHistory();
  const { data: vendors } = useVendors();
  const { data: cBudget } = useCommitteeBudget();
  const { data: analysis } = useFinanceAnalysis();

  // 1) COMMITTEE variance — the ask on file vs the proposed FY27 v2 budget
  // (06-JUL-2026 revision; Board adoption pending). See committeeVarianceView:
  // comparable committees only, and an honest "not computable" when none are.
  const cLoaded = cBudget != null;
  const committee = useMemo(
    () => committeeVarianceView(cBudget?.lines),
    [cBudget],
  );
  const committeeVar = committee.variances;

  // 2) OCA projected overruns — projected landing (actual + open encumbrance) ≥
  // budget, INCLUDING objects with no appropriation at all (see overrunRows).
  const overrun = useMemo(
    () => overrunRows(watch?.data as ObjectWatchRow[] | undefined),
    [watch],
  );
  const overruns = overrun.rows;

  // Current/prior FY for the OBJECT history (YoY swings) — derived from the data
  // (newest fiscal_year present) so it rolls to FY27 automatically on 7/1, with a
  // clock-based fallback when the dataset is empty.
  const objRows = useMemo(
    () => (history?.object_rows ?? []) as HistoryObjectRow[],
    [history],
  );
  const objCurFy =
    maxFiscalYear(objRows.map((r) => r.fiscal_year)) ?? currentFiscalYear();
  const objPriorFy = priorFiscalYear(objCurFy);
  // The YoY / vendor comparison cards read HISTORICAL sources that hold complete
  // fiscal years only (dash_finance_history is FY20–FY26 by design; the PO
  // register is refreshed ~weekly and currently carries FY26 rows). That is NOT
  // the same as "the county hasn't loaded FY27" — FY27/2119 IS live on the levy
  // pages — so the disclosure uses the 'historical-source' banner variant and
  // lives ON the comparison card (scoped, honest), not page-wide asserting
  // FY26-everything while the committee/OCA cards above it are FY27 data.
  const clockFy = currentFiscalYear();
  const alertsFyc = fyContext(objCurFy);

  // 3) Big YoY object swings (current FY vs prior FY actual)
  const yoy = useMemo(() => {
    const m = new Map<string, { desc: string; prior: number; cur: number }>();
    for (const r of objRows) {
      const e = m.get(r.object_segment) ?? {
        desc: r.object_desc ?? "",
        prior: 0,
        cur: 0,
      };
      if (r.fiscal_year === objPriorFy) e.prior = asNum(r.actual_amount);
      if (r.fiscal_year === objCurFy) e.cur = asNum(r.actual_amount);
      m.set(r.object_segment, e);
    }
    return [...m.entries()]
      .map(([obj, e]) => ({
        obj,
        ...e,
        delta: e.cur - e.prior,
        pct:
          e.prior > 0 ? Math.round(((e.cur - e.prior) / e.prior) * 100) : null,
      }))
      .filter(
        (r) =>
          Math.abs(r.delta) >= 25_000 &&
          (r.pct === null || Math.abs(r.pct) >= 25),
      )
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
      .slice(0, 12);
  }, [objRows, objCurFy, objPriorFy]);

  // Latest FY present in the PO register — labels the vendor card honestly
  // (the register is refreshed ~weekly and lags the clock at FY boundaries).
  const vendorRows = useMemo(
    () => (vendors?.rows ?? []) as VendorRow[],
    [vendors],
  );
  const vendorCurFy =
    maxFiscalYear(vendorRows.map((r) => r.fiscal_year)) ?? currentFiscalYear();

  // 4) Vendor anomalies — from the scheduled finance-analysis engine
  // (/api/finance/analysis), NOT a raw SUM over dash_finance_po. The engine
  // de-dups re-issued PO copies, strips one-time capital lines, and requires a
  // real trailing baseline before calling anything a spike — so the $1.376M
  // Seagrave pumper ($1.2M of it CARP county capital) can never surface
  // Williams here (pumper-CARP-split control). A naive client-side SUM did
  // exactly that. Resolved signals are history, not open alerts.
  const vendorSignals = useMemo(
    () =>
      ((analysis?.signals ?? []) as FinanceSignal[])
        .filter(
          (s) =>
            s.category === "vendor_anomaly" && s.delta_state !== "resolved",
        )
        .map((s) => {
          const cur = asNum(s.metrics?.latest_fy_spend);
          const avg = asNum(s.metrics?.trailing_avg);
          return {
            supplier: s.subject_label ?? s.subject_key.replace(/^vendor:/, ""),
            severity: s.severity,
            message: s.message,
            cur,
            avg,
            delta: cur - avg,
            // info = "new material vendor" note (no real baseline → no ratio)
            isNew: s.severity === "info",
          };
        }),
    [analysis],
  );
  const analysisRunAt = analysis?.run?.run_at
    ? String(analysis.run.run_at).slice(0, 10)
    : null;

  // NOTE: the committee-variance denominator is the v2 BASELINE population
  // (committee.committeesTotal), not the PO register's committee list — the two
  // are different populations and mixing them mislabels the coverage.

  // ── Verdict banner — derived live. "Act now" = OCA objects projected to
  // overrun (red). "Watch" = committee/YoY/vendor variances (amber). The
  // sentence tells the board how many of each before they read the tables.
  // The committee lens is EXCLUDED from any all-clear wording while it is not
  // computable — the banner must never assert committees are within range on
  // the strength of a check that did not run.
  const committeeUnchecked = cLoaded && !committee.computable;
  const committeeCaveat = committeeUnchecked
    ? " Committee ask variance is NOT included — no comparable FY27 ask is on file (see below)."
    : "";
  const actNowCount = overruns.length;
  const watchCount = committeeVar.length + yoy.length + vendorSignals.length;
  const verdictTone: VerdictTone =
    actNowCount > 0
      ? "red"
      : watchCount > 0 || committeeUnchecked
        ? "amber"
        : "green";
  const verdictText =
    actNowCount > 0
      ? `${actNowCount} object${actNowCount === 1 ? "" : "s"} projected to overrun budget — act now. ${watchCount} more item${watchCount === 1 ? "" : "s"} to watch.${committeeCaveat}`
      : watchCount > 0
        ? `Nothing is projected to overrun budget. ${watchCount} item${watchCount === 1 ? "" : "s"} worth a look — big swings, vendor spikes${committeeUnchecked ? "" : ", and committee asks"}.${committeeCaveat}`
        : committeeUnchecked
          ? `Nothing flagged on the object, year-over-year, or vendor lenses.${committeeCaveat}`
          : "Nothing flagged — every committee, object, and vendor is within its expected range.";

  return (
    <PageShell>
      <div>
        <PageHeader
          eyebrow="FINANCE"
          title="Variance & Alerts"
          subtitle="The exceptions worth the board's attention, by budget lens — committee asks that diverge from the data, OCA objects projected to overrun, big year-over-year swings, and vendor spikes. Talk to these, not the full ledger."
        />
        <PageIntro tone={verdictTone}>{verdictText}</PageIntro>
        <StatusLegend
          className="mt-3"
          items={[
            { dot: "bg-status-red", label: "Act now — projected to overrun" },
            { dot: "bg-status-amber", label: "Watch — variance worth a look" },
          ]}
        />
      </div>

      <FetchErrorBanner error={error} what="variance & alerts" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          label={
            <span className="inline-flex items-center gap-1">
              Committee Variances <InfoTip term="variance" />
            </span>
          }
          value={committee.computable ? committeeVar.length : "—"}
          subtitle={
            committee.computable
              ? `${committee.comparable} of ${committee.committeesTotal} committees, ask vs proposed v2 budget`
              : cLoaded
                ? "not computable — no comparable FY27 ask on file"
                : "loading…"
          }
          accent={
            !committee.computable
              ? "amber"
              : committeeVar.length
                ? "amber"
                : "green"
          }
          isLoading={!cLoaded}
        />
        <KPICard
          label={
            <span className="inline-flex items-center gap-1">
              OCA Projected Overruns <InfoTip term="oca" />
            </span>
          }
          value={overruns.length}
          subtitle={
            overrun.unbudgeted.length
              ? `≥100% of budget incl. encumbrance — incl. ${overrun.unbudgeted.length} unbudgeted (${usd0(overrun.unbudgetedTotal)})`
              : "≥100% of budget incl. encumbrance"
          }
          accent={overruns.length ? "red" : "green"}
          isLoading={isLoading}
        />
        <KPICard
          label="Big YoY Swings"
          value={yoy.length}
          subtitle={`≥$25K & ≥25% vs ${objPriorFy}`}
          accent={yoy.length ? "amber" : "green"}
        />
        <KPICard
          label="Vendor Anomalies"
          value={vendorSignals.length}
          subtitle={`${vendorCurFy} vs trailing avg, engine-vetted`}
          accent={vendorSignals.length ? "amber" : "green"}
        />
      </div>

      {/* Committee budget variance */}
      <ChartCard
        title={
          <StatusTitle tone="amber">Committee Budget Variance</StatusTitle>
        }
        subtitle="FY27 ask (operating + projects) vs the FY27 v2 budget (06-JUL revision — formal adoption pending) — where committees diverge from the proposed budget"
        empty={committee.computable && committeeVar.length === 0}
        minHeight={100}
      >
        <p className="mb-3 text-xs text-theme-muted">
          Baseline: the proposed FY27 v2 budget (06-JUL-2026 revision; Board
          adoption pending), served live from the v2 lines — full detail on{" "}
          <Link to="/finance/fy27-v2" className="underline font-medium">
            FY27 Budget v2
          </Link>
          .
        </p>
        {!cLoaded ? (
          <p className="px-1 py-3 text-sm text-theme-muted">
            Loading committee asks…
          </p>
        ) : !committee.computable ? (
          <CommitteeVarianceUnavailable view={committee} />
        ) : (
          <div className="overflow-x-auto -mx-4 md:-mx-5">
            <table className="w-full min-w-[680px] text-left">
              <caption className="sr-only">Committee budget variance</caption>
              <thead>
                <tr className="border-b border-theme-subtle">
                  <th scope="col" className={TH}>
                    Committee / Office
                  </th>
                  <th scope="col" className={THR}>
                    v2 baseline (adoption pending)
                  </th>
                  <th scope="col" className={THR}>
                    FY27 Ask
                  </th>
                  <th scope="col" className={THR}>
                    <span className="inline-flex items-center gap-1">
                      Variance <InfoTip term="variance" />
                    </span>
                  </th>
                  <th scope="col" className={`${TH} text-center`}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {committeeVar.map((r) => (
                  <tr
                    key={r.committee}
                    className="hover:bg-theme-overlay/50 transition-colors"
                  >
                    <td className="px-3 py-2 text-sm text-theme-primary">
                      {r.committee}
                    </td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums text-theme-secondary">
                      {usd0(r.baseline)}
                    </td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums text-theme-secondary">
                      {usd0(r.ask)}
                    </td>
                    <td
                      className={`px-3 py-2 text-sm text-right tabular-nums font-semibold ${r.delta > 0 ? "text-status-red" : "text-status-green"}`}
                    >
                      {r.delta >= 0 ? "+" : ""}
                      {usd0(r.delta)}
                      {r.pct !== 999
                        ? ` (${r.pct >= 0 ? "+" : ""}${r.pct}%)`
                        : ""}
                    </td>
                    <td className="px-3 py-2 text-center text-xs text-theme-muted capitalize">
                      {r.status ?? "—"}
                    </td>
                  </tr>
                ))}
                {committeeVar.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-3 py-4 text-sm text-status-green"
                    >
                      None of the {committee.comparable} committee ask
                      {committee.comparable === 1 ? "" : "s"} that can be
                      compared diverge from the proposed FY27 v2 budget
                      (06-JUL-2026 revision). ✓
                      {committee.comparable < committee.committeesTotal && (
                        <span className="block mt-1 text-xs text-status-amber">
                          {committee.committeesTotal - committee.comparable} of{" "}
                          {committee.committeesTotal} committees are NOT covered
                          by this check —{" "}
                          {committee.uncomparableCommittees.join(", ")}.
                        </span>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </ChartCard>

      {/* OCA projected overruns */}
      <ChartCard
        title={
          <StatusTitle tone="red">OCA Objects Projected to Overrun</StatusTitle>
        }
        subtitle="Actual spend + open encumbrance ≥ budget — forward-looking, not just consumed. Objects with NO appropriation and real spend are included and labelled unbudgeted."
        empty={!isLoading && overruns.length === 0}
        minHeight={100}
      >
        {overrun.unbudgeted.length > 0 && (
          <p className="mb-3 rounded-lg border border-status-red/40 bg-status-red/5 px-3 py-2 text-xs text-theme-secondary">
            <span className="font-semibold text-status-red">
              {overrun.unbudgeted.length} unbudgeted object
              {overrun.unbudgeted.length === 1 ? "" : "s"} —{" "}
              {usd0(overrun.unbudgetedTotal)} projected against a $0
              appropriation.
            </span>{" "}
            Spend on an object with no budget line has no appropriation behind
            it at all; it is shown here rather than omitted for having nothing
            to overrun.
          </p>
        )}
        <div className="overflow-x-auto -mx-4 md:-mx-5">
          <table className="w-full min-w-[720px] text-left">
            <caption className="sr-only">
              OCA objects projected to overrun budget
            </caption>
            <thead>
              <tr className="border-b border-theme-subtle">
                <th scope="col" className={TH}>
                  <span className="inline-flex items-center gap-1">
                    Object <InfoTip term="object" />
                  </span>
                </th>
                <th scope="col" className={TH}>
                  Description
                </th>
                <th scope="col" className={THR}>
                  Budget
                </th>
                <th scope="col" className={THR}>
                  Spent
                </th>
                <th scope="col" className={THR}>
                  <span className="inline-flex items-center gap-1">
                    Encumbered <InfoTip term="encumbrance" />
                  </span>
                </th>
                <th scope="col" className={THR}>
                  Projected
                </th>
              </tr>
            </thead>
            <tbody>
              {overruns.map((r) => (
                <tr
                  key={r.object_segment}
                  className="hover:bg-theme-overlay/50 transition-colors"
                >
                  <td className="px-3 py-2 text-sm font-mono text-theme-primary">
                    {r.object_segment}
                  </td>
                  <td
                    className="px-3 py-2 text-sm text-theme-secondary max-w-[220px] truncate"
                    title={r.object_desc}
                  >
                    {r.object_desc}
                  </td>
                  <td className="px-3 py-2 text-sm text-right tabular-nums text-theme-secondary">
                    {asNum(r.budget) > 0 ? (
                      usd0(r.budget)
                    ) : (
                      <span className="text-status-red font-semibold">
                        {usd0(0)} · unbudgeted
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm text-right tabular-nums text-theme-secondary">
                    {usd0(r.actual)}
                  </td>
                  <td className="px-3 py-2 text-sm text-right tabular-nums text-status-amber">
                    {usd0(r.open_encumbrance)}
                  </td>
                  <td className="px-3 py-2 text-sm text-right tabular-nums font-semibold text-status-red">
                    {usd0(r.projected)}{" "}
                    {asNum(r.budget) > 0
                      ? r.pct_projected <= 900
                        ? `(${r.pct_projected}%)`
                        : ""
                      : "(no appropriation)"}
                  </td>
                </tr>
              ))}
              {!isLoading && overruns.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-4 text-sm text-status-green"
                  >
                    No objects projected to overrun. ✓
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </ChartCard>

      <ChartCard
        title={
          <StatusTitle tone="amber">Year-over-Year Object Swings</StatusTitle>
        }
        subtitle={`${objCurFy} vs ${objPriorFy} actual, ≥$25K change`}
        empty={yoy.length === 0}
        minHeight={100}
      >
        <PriorYearBanner
          ctx={alertsFyc}
          variant="historical-source"
          className="mb-3"
        />
        <div className="overflow-x-auto -mx-4 md:-mx-5">
          <table className="w-full min-w-[640px] text-left">
            <caption className="sr-only">
              Year-over-year object spend swings
            </caption>
            <thead>
              <tr className="border-b border-theme-subtle">
                <th scope="col" className={TH}>
                  Object
                </th>
                <th scope="col" className={TH}>
                  Description
                </th>
                <th scope="col" className={THR}>
                  {objPriorFy}
                </th>
                <th scope="col" className={THR}>
                  {objCurFy}
                </th>
                <th scope="col" className={THR}>
                  Change
                </th>
              </tr>
            </thead>
            <tbody>
              {yoy.map((r) => (
                <tr
                  key={r.obj}
                  className="hover:bg-theme-overlay/50 transition-colors"
                >
                  <td className="px-3 py-2 text-sm font-mono text-theme-primary">
                    {r.obj}
                  </td>
                  <td
                    className="px-3 py-2 text-sm text-theme-secondary max-w-[240px] truncate"
                    title={r.desc}
                  >
                    {r.desc}
                  </td>
                  <td className="px-3 py-2 text-sm text-right tabular-nums text-theme-secondary">
                    {usd0(r.prior)}
                  </td>
                  <td className="px-3 py-2 text-sm text-right tabular-nums text-theme-secondary">
                    {usd0(r.cur)}
                  </td>
                  <td
                    className={`px-3 py-2 text-sm text-right tabular-nums font-semibold ${r.delta > 0 ? "text-status-red" : "text-status-green"}`}
                  >
                    {r.delta >= 0 ? "+" : ""}
                    {usd0(r.delta)}
                    {r.pct !== null
                      ? ` (${r.pct >= 0 ? "+" : ""}${r.pct}%)`
                      : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      <ChartCard
        title={<StatusTitle tone="amber">Vendor Spend Anomalies</StatusTitle>}
        subtitle={`Scheduled-analysis signals${analysisRunAt ? ` (computed ${analysisRunAt})` : ""} — re-issued POs de-duped, one-time capital stripped, real trailing baseline required. Source: PO register, ${vendorCurFy} rows, refreshed ~weekly — not live ${clockFy} spend.`}
        empty={vendorSignals.length === 0}
        minHeight={100}
      >
        <div className="overflow-x-auto -mx-4 md:-mx-5">
          <table className="w-full min-w-[640px] text-left">
            <caption className="sr-only">Vendor spend anomalies</caption>
            <thead>
              <tr className="border-b border-theme-subtle">
                <th scope="col" className={TH}>
                  Vendor
                </th>
                <th scope="col" className={THR}>
                  Prior Avg
                </th>
                <th scope="col" className={THR}>
                  {vendorCurFy}
                </th>
                <th scope="col" className={THR}>
                  Spike
                </th>
              </tr>
            </thead>
            <tbody>
              {vendorSignals.map((r) => (
                <tr
                  key={r.supplier}
                  className="hover:bg-theme-overlay/50 transition-colors"
                >
                  <td
                    className="px-3 py-2 text-sm text-theme-primary max-w-[280px] truncate"
                    title={r.message}
                  >
                    {r.supplier}
                  </td>
                  <td className="px-3 py-2 text-sm text-right tabular-nums text-theme-secondary">
                    {r.isNew ? "—" : usd0(r.avg)}
                  </td>
                  <td className="px-3 py-2 text-sm text-right tabular-nums text-theme-secondary">
                    {usd0(r.cur)}
                  </td>
                  {r.isNew ? (
                    <td className="px-3 py-2 text-xs text-right text-theme-muted">
                      new vendor — no baseline
                    </td>
                  ) : (
                    <td
                      className={`px-3 py-2 text-sm text-right tabular-nums font-semibold ${r.severity === "critical" ? "text-status-red" : "text-status-amber"}`}
                    >
                      +{usd0(r.delta)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </PageShell>
  );
}
